import * as React from "react";
import { ComponentClass, createElement, Fragment, FunctionComponent, HtmlHTMLAttributes, ReactElement, ReactNode, useCallback, useMemo } from "react";
import {
  DragDropContext,
  Draggable,
  DraggableChildrenFn,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import { genericMemo } from "../../utils";
import { SortableItemContainer, SortableItemContainerProps } from "./SortableItemContainer";
import { SortableItemProps } from "./types";
import { reorder } from "./utils";

export type SortableListProps<T, CP extends SortableItemProps = SortableItemContainerProps> = Omit<HtmlHTMLAttributes<HTMLDivElement>, "onChange"> & {
  value: T[],
  filterValues?: (v: T) => boolean,
  onChange: (v: T[]) => void,
  renderItem: (T: T, index: number) => ReactNode,
  getItemId: (T: T) => string,
  renderClone?: boolean,
  containerComponent?: FunctionComponent<CP> | ComponentClass<CP>,
  containerProps?: CP,
  disabled?: boolean
}

export const SortableList = genericMemo(<T extends any, CP extends SortableItemProps = SortableItemContainerProps>(
  {
    value,
    filterValues = () => true,
    onChange,
    renderItem = (v) => v as ReactNode,
    getItemId = (v) => `${v}`,
    renderClone,
    containerComponent = SortableItemContainer,
    containerProps,
    disabled = false,
    ...props
  }: SortableListProps<T, CP>) => {

  const onDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) {
      return;
    }
    const items = reorder(value, filterValues, result.source.index, result.destination.index);
    return onChange?.(items);
  }, [ value, onChange, filterValues ]);

  const renderItemInternal = useCallback((
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot,
    node: ReactNode,
    key: string
  ): ReactElement => {
    return createElement(
      containerComponent,
      {
        ref: provided.innerRef,
        disabled,
        isDragging: snapshot.isDragging,
        draggableProps: provided.draggableProps,
        dragHandleProps: provided.dragHandleProps,
        ...containerProps,
        key
      } as unknown as CP,
      node,
    );
  }, [ containerComponent, disabled, containerProps ]);

  const renderCloneFunc = useMemo((): DraggableChildrenFn | undefined => {
    if (renderClone) {
      return (provided, snapshot, rubric) => renderItemInternal(
        provided,
        snapshot,
        renderItem(value.filter(filterValues)[rubric.source.index], rubric.source.index),
        getItemId(value.filter(filterValues)[rubric.source.index])
      );
    }
    return undefined;
  }, [ renderClone, renderItemInternal, renderItem, value, filterValues, getItemId ]);

  return (
    <DragDropContext key={"DragDropContext"} onDragEnd={onDragEnd}>
      <Droppable
        key={"Droppable"}
        droppableId="droppable"
        renderClone={renderCloneFunc}
      >
        {(provided) => (
          <div
            key={"droppable"}
            {...props}
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {
              value.filter(filterValues)
                .map((item, index) => (
                  <Draggable
                    key={getItemId(item)}
                    draggableId={getItemId(item)}
                    isDragDisabled={disabled}
                    index={index}
                  >
                    {
                      (provided, snapshot) =>
                        renderItemInternal(provided, snapshot, renderItem(item, index), getItemId(item))
                    }
                  </Draggable>
                ))}
            <Fragment key="placeholder">
              {provided.placeholder}
            </Fragment>
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
});